/**
 * Theme Manager - Easy color and theme access for React components
 * Single source of truth: reads colors from SCSS CSS custom properties
 * Colors are defined only in _bootstrap-variables.scss
 */

import { useSelector } from 'react-redux';
import { hexToRGBA } from '../../Shared/Utils';

class ThemeManager {
  /**
   * Get semantic colors for component usage
   * @param {Object} customPalette - Optional custom palette from Redux (overrides defaults)
   * @returns {Object} Semantic color object
   */
  getColors(customPalette = {}) {
    // Get current CSS custom properties (from SCSS)
    const cssColors = this.getCurrentCSSColors();

    // Override with customPalette if provided
    const overrides = this.mapCustomPaletteColors(customPalette);

    const merged = {
      ...cssColors,
      ...overrides
    };

    // Auto-generate contrast text colors for WCAG AA compliance
    merged.primaryText = this.getContrastText(merged.primary);
    merged.successText = this.getContrastText(merged.success);
    merged.infoText = this.getContrastText(merged.info);
    merged.warningText = this.getContrastText(merged.warning);
    merged.accentText = this.getContrastText(merged.accent);

    return merged;
  }

  getContrastText(color) {
    if (!color) return '#ffffff';
    let r, g, b;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const full = hex.length === 3
        ? hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2]
        : hex;
      r = parseInt(full.substr(0, 2), 16);
      g = parseInt(full.substr(2, 2), 16);
      b = parseInt(full.substr(4, 2), 16);
    } else if (color.startsWith('rgb')) {
      [r, g, b] = color.match(/\d+/g).map(Number);
    } else {
      return '#ffffff';
    }
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  }

  /**
   * Apply customPalette colors to CSS custom properties (one-time setup)
   * @param {Object} customPalette - Custom palette from Redux
   */
  applyCustomPalette(customPalette = getDefaultBrandColors()) {
    const mappedColors = this.mapCustomPaletteColors(customPalette);
    const root = document.documentElement;

    // Only override the CSS custom properties that have values
    Object.entries(mappedColors).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--color-${this.camelToKebab(key)}`, value);
      }
    });

    // Set auto-contrast text colors as CSS variables
    const contrastPairs = { primary: mappedColors.primary, success: mappedColors.success, info: mappedColors.info, warning: mappedColors.warning, accent: mappedColors.accent };
    Object.entries(contrastPairs).forEach(([key, bg]) => {
      if (bg) {
        root.style.setProperty(`--color-${key}-text`, this.getContrastText(bg));
      }
    });
  }

  /**
   * Get current CSS color values from computed styles (reads from SCSS)
   * @returns {Object} Current CSS colors
   */
  getCurrentCSSColors() {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    return {
      primary: computedStyle.getPropertyValue('--color-primary').trim() || '#00c2d1',
      secondary: computedStyle.getPropertyValue('--color-secondary').trim() || '#3c4d73',
      accent: computedStyle.getPropertyValue('--color-accent').trim() || '#33b1e6',
      success: computedStyle.getPropertyValue('--color-success').trim() || '#00dec4',
      info: computedStyle.getPropertyValue('--color-info').trim() || '#30d7ed',
      warning: computedStyle.getPropertyValue('--color-warning').trim() || '#fd7e14',
      error: computedStyle.getPropertyValue('--color-error').trim() || '#dc3545',
      primaryDark: computedStyle.getPropertyValue('--color-primary-dark').trim() || '#017992',
      accentLight: computedStyle.getPropertyValue('--color-accent-light').trim() || '#b8ebff',
      surface: computedStyle.getPropertyValue('--color-surface').trim() || '#fdfefe',
    };
  }

  /**
   * Map Redux customPalette to our semantic color system
   * Excludes red, orange, gray as they never change
   * @param {Object} customPalette - Custom palette from Redux state
   * @returns {Object} Mapped semantic colors
   */
  mapCustomPaletteColors(customPalette) {
    const result = {};
    
    // Map customPalette properties to semantic colors
    // Skip red, orange, gray as mentioned they never change
    if (customPalette.primary) result.primary = customPalette.primary;
    if (customPalette.secondary) result.secondary = customPalette.secondary;
    if (customPalette.accent || customPalette.highlight) {
      result.accent = customPalette.accent || customPalette.highlight;
    }
    if (customPalette.success || customPalette.green) {
      result.success = customPalette.success || customPalette.green;
    }
    if (customPalette.info || customPalette.blue) {
      result.info = customPalette.info || customPalette.blue;
    }
    if (customPalette.surface || customPalette.background) {
      result.surface = customPalette.surface || customPalette.background;
    }
    if (customPalette.primaryDark) {
      result.primaryDark = customPalette.primaryDark;
    }
    if (customPalette.accentLight) {
      result.accentLight = customPalette.accentLight;
    }

    // Handle themeColor from appTheme (like in ChartWidget)
    if (customPalette.themeColor) {
      const themeColor = customPalette.themeColor.replace("0x", "#");
      result.primary = themeColor;
    }

    // Auto-generate variants if not provided but primary colors exist
    if (result.primary && !customPalette.primaryDark) {
      result.primaryDark = this.darkenColor(result.primary, 0.2);
    }
    if (result.accent && !customPalette.accentLight) {
      result.accentLight = this.lightenColor(result.accent, 0.3);
    }
    
    return result;
  }

  /**
   * Convert camelCase to kebab-case
   * @param {string} str 
   * @returns {string}
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Darken a hex color by a percentage
   * @param {string} hex - Hex color code
   * @param {number} percent - Amount to darken (0-1)
   * @returns {string} Darkened hex color
   */
  darkenColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent * 100);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  /**
   * Lighten a hex color by a percentage
   * @param {string} hex - Hex color code  
   * @param {number} percent - Amount to lighten (0-1)
   * @returns {string} Lightened hex color
   */
  lightenColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent * 100);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  /**
   * Generate a complete color palette from a single primary color
   * @param {string} primaryColor - Primary color in hex format
   * @returns {Object} Complete color palette with semantic colors
   */
  generatePaletteFromPrimary(primaryColor) {
    const primary = primaryColor.replace('#', '');
    
    // Convert hex to HSL for better color manipulation
    const hexToHsl = (hex) => {
      const r = parseInt(hex.substr(0, 2), 16) / 255;
      const g = parseInt(hex.substr(2, 2), 16) / 255;
      const b = parseInt(hex.substr(4, 2), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0; // achromatic
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }
      return [h * 360, s * 100, l * 100];
    };

    // Convert HSL back to hex
    const hslToHex = (h, s, l) => {
      h = h / 360;
      s = s / 100;
      l = l / 100;

      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      const r = hue2rgb(p, q, h + 1/3);
      const g = hue2rgb(p, q, h);
      const b = hue2rgb(p, q, h - 1/3);

      const toHex = (c) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const [h, s, l] = hexToHsl(primary);

    // Generate palette - start with brand colors, then override specific ones
    // Keep same hue (h) for all colors to maintain color family consistency
    return {
      ...getDefaultBrandColors(),
      primary: `#${primary}`,
      secondary: hslToHex(h, Math.min(s * 0.9, 90), Math.max(l * 0.6, 25)), // Darker version of primary, same hue
      accent: hslToHex(h, Math.min(s * 0.8, 80), Math.min(l * 1.2, 70)), // Lighter version, same hue
      info: hslToHex(h, Math.min(s * 0.6, 75), Math.min(l * 1.3, 75)), // Even lighter version, same hue
      primaryDark: this.darkenColor(`#${primary}`, 0.2),
      accentLight: this.lightenColor(`#${primary}`, 0.3), // Light version of primary
    };
  }
}

// Export singleton instance and factory functions for easy component usage
const themeManager = new ThemeManager();

// Easy helper functions for components
// When called without parameters, reads from CSS custom properties (already set globally)
export const getColors = (customPalette) => {
  if (customPalette === undefined) {
    // No parameter provided - read from current CSS custom properties
    return themeManager.getCurrentCSSColors();
  }
  return themeManager.getColors(customPalette);
};
export const applyCustomPalette = (customPalette = getDefaultBrandColors()) => themeManager.applyCustomPalette(customPalette);

// Universal color access - works in both function and class components
// For function components: const colors = useColors();
// For class components: const colors = getColors(); (in render method)
export const useColors = (customPalette) => {
  try {
    const reduxPalette = useSelector(state => state.user?.data?.appTheme?.customPalette);
    const paletteToUse = customPalette || reduxPalette || {};
    return themeManager.getColors(paletteToUse);
  } catch (error) {
    // Fallback if Redux isn't available (e.g., component used outside Provider)
    return themeManager.getColors(customPalette || {});
  }
};

// Get default brand colors (hardcoded SCSS fallback values - never affected by themes)
export const getDefaultBrandColors = () => {
  return {
    primary: '#00c2d1',        // Brand teal
    secondary: '#3c4d73',      // Dark navy
    accent: '#33b1e6',         // Medium blue
    success: '#00dec4',        // Bright green
    info: '#30d7ed',           // Light cyan
    warning: '#fd7e14',        // Orange
    error: '#dc3545',          // Red
    primaryDark: '#017992',    // Deep teal
    accentLight: '#b8ebff',    // Light blue
    surface: '#fdfefe',        // Light gray
    textMuted: '#6c757d',      // Text muted gray
    // Complete gray scale from SCSS
    gray50: '#f8f9fa',         // Lightest background
    gray100: '#f0f3f5',        // Very light
    gray150: '#e9ecef',        // Light borders
    gray200: '#e0e6e8',        // Light
    gray250: '#ced4da',        // Medium borders
    gray300: '#e0e6e8',        // Light gray
    gray350: '#adb5bd',        // Disabled text
    gray400: '#869fac',        // Medium gray
    gray450: '#6c757d',        // Secondary text
    gray500: '#678898',        // Medium dark gray
    gray550: '#495057',        // Body text
    gray600: '#536c79',        // Dark gray
    gray700: '#3e515b',        // Darker gray
    gray800: '#29363d',        // Very dark gray
    gray850: '#212529',        // Headings
    gray900: '#151b1e',        // Darkest gray
    white: '#fff',             // White
    black: '#000',             // Black
  };
};

// Generate a dynamic color palette from a single primary color
export const generatePaletteFromPrimary = (primaryColor) => {
  if (!primaryColor) return;
  return themeManager.generatePaletteFromPrimary(primaryColor);
};

/**
 * Gets CSS custom properties object for sidebar active state
 * Uses CSS custom properties that should already be set globally
 * @returns {Object} Object with CSS custom properties
 */
export const getSidebarActiveStyles = () => {
  return {
    "--sidebar-active-border-color": "var(--color-primary)",
    "--sidebar-active-bg-color": "var(--color-primary-bg)",
  };
};

// Re-export hexToRGBA from Shared/Utils for convenience
export { hexToRGBA } from '../../Shared/Utils';

export default themeManager;